import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import Excel from 'exceljs';
import FileSaver from 'file-saver';
import { GetPayrollEntriesQuery } from '../../../../services';

type TEntry = GetPayrollEntriesQuery['getPayrollEntries'][number];

const MONEY_FMT = '#,##0.00';

// Column order MUST mirror the on-screen table (payroll-entries-page.tsx),
// minus the actions column. Keeping a single ordered list of [header, picker]
// pairs avoids the header/row drift that creeps in when the two are separate.
type ColDef = {
    header: string;
    // null cells stay blank in Excel instead of writing a 0.
    value: (e: TEntry, employeeName: string) => string | number | null;
    text?: boolean; // text columns left-align; numeric columns right-align + money fmt
};

const COLUMNS: ColDef[] = [
    { header: 'Empleado', value: (_e, name) => name, text: true },
    { header: 'Estatus', value: (e) => e.estatus, text: true },
    { header: 'Área', value: (e) => e.area, text: true },
    { header: 'Sueldo', value: (e) => e.sueldo },
    { header: 'JO', value: (e) => e.jo },
    { header: 'HT', value: (e) => e.ht },
    // Effective HE: the manual override when set, otherwise the auto formula.
    { header: 'HE', value: (e) => (e.he_override ? e.he : e.he_effective) },
    { header: 'Ret.', value: (e) => e.retardos },
    { header: 'Falt.', value: (e) => e.faltas },
    { header: '$HN', value: (e) => e.hn },
    { header: '$HE', value: (e) => e.he_pay },
    { header: '$Fest', value: (e) => e.fest_pay },
    { header: '$Vac', value: (e) => e.vac_pay },
    { header: '$PrimaV', value: (e) => e.prima_vac },
    { header: 'Punt.', value: (e) => e.puntualidad },
    { header: 'Asist.', value: (e) => e.asistencia },
    { header: 'BP', value: (e) => e.bp },
    { header: 'BD', value: (e) => e.bd },
    { header: 'Entregas', value: (e) => e.entregas_especiales },
    { header: 'Viajes', value: (e) => e.viajes },
    { header: 'Bonos', value: (e) => e.bonos },
    { header: 'Otros+', value: (e) => e.otros_mas },
    { header: 'INFONAVIT', value: (e) => e.infonavit },
    { header: 'FONACOT', value: (e) => e.fonacot },
    { header: 'Préstamos', value: (e) => e.descuento_prestamos },
    { header: 'Otros-', value: (e) => e.otros_menos },
    { header: 'Total+', value: (e) => e.total_plus },
    { header: 'Total-', value: (e) => e.total_minus },
    { header: 'TOTAL', value: (e) => e.total },
    { header: 'Obs.', value: (e) => e.observaciones, text: true },
];

const TOTAL_COL_INDEX = COLUMNS.findIndex((c) => c.header === 'TOTAL') + 1; // 1-based

// --- Paper fit ---------------------------------------------------------------
// We do NOT try to make the column character-widths literally add up to 8.5in.
// Instead we let Excel's own Page Setup scale the sheet: "fit to 1 page wide".
// That keeps every column on a single Carta (Letter) page width regardless of
// the actual widths, with the margins below acting as the printer padding.
const LETTER_PAPER_SIZE = 1; // ExcelJS paperSize code: 1 = US Letter / Carta
const PAGE_SETUP: Partial<Excel.PageSetup> = {
    paperSize: LETTER_PAPER_SIZE,
    orientation: 'landscape', // 30 columns: portrait would be unreadable
    fitToPage: true,
    fitToWidth: 1, // squeeze all columns onto one page across
    fitToHeight: 0, // 0 = unlimited pages tall (rows flow naturally)
    horizontalCentered: true,
    margins: {
        // inches — the "padding" frequently used by printers
        left: 0.5,
        right: 0.5,
        top: 0.5,
        bottom: 0.5,
        header: 0.3,
        footer: 0.3,
    },
};

// Widen each column to fit its longest cell (with padding), capped so the
// fit-to-width scaling above doesn't have to shrink text too aggressively.
function autoSizeColumns(ws: Excel.Worksheet) {
    ws.columns.forEach((_c, i) => {
        const column = ws.getColumn(i + 1);
        let maxLen = 6;
        column.eachCell({ includeEmpty: true }, (cell) => {
            const len = cell.value ? cell.value.toString().length : 0;
            maxLen = Math.max(maxLen, len);
        });
        column.width = Math.min(maxLen + 2, 28);
    });
}

export function PayrollEntriesExcelExportButton({
    entries,
    employeeName,
    grandTotal,
    fileName,
    disabled,
}: {
    entries: TEntry[];
    employeeName: (employeeId: number) => string;
    grandTotal: number;
    fileName: string;
    disabled?: boolean;
}) {
    return (
        <Tooltip title="Descargar Excel">
            <span>
                <IconButton
                    size="small"
                    disabled={disabled || entries.length === 0}
                    onClick={async () => {
                        const workbook = new Excel.Workbook();
                        const ws = workbook.addWorksheet('Nómina', {
                            pageSetup: PAGE_SETUP,
                            views: [{ state: 'frozen', xSplit: 1, ySplit: 1 }],
                        });

                        // Header row.
                        const headerRow = ws.addRow(COLUMNS.map((c) => c.header));
                        headerRow.font = { bold: true };
                        headerRow.alignment = { vertical: 'middle', wrapText: true };

                        // Data rows.
                        entries.forEach((e) => {
                            ws.addRow(
                                COLUMNS.map((c) =>
                                    c.value(e, employeeName(e.employee_id)),
                                ),
                            );
                        });

                        // Per-column formatting: money + alignment.
                        COLUMNS.forEach((c, i) => {
                            const column = ws.getColumn(i + 1);
                            if (c.text) {
                                column.alignment = { horizontal: 'left' };
                            } else {
                                column.numFmt = MONEY_FMT;
                                column.alignment = { horizontal: 'right' };
                            }
                        });

                        // Grand-total row: label under "Préstamos" area, value under TOTAL.
                        ws.addRow([]);
                        const totalRow = ws.addRow([]);
                        const labelCell = totalRow.getCell(TOTAL_COL_INDEX - 1);
                        labelCell.value = 'Gran Total';
                        labelCell.font = { bold: true };
                        labelCell.alignment = { horizontal: 'right' };
                        const valueCell = totalRow.getCell(TOTAL_COL_INDEX);
                        valueCell.value = grandTotal;
                        valueCell.numFmt = MONEY_FMT;
                        valueCell.font = { bold: true };
                        valueCell.alignment = { horizontal: 'right' };

                        autoSizeColumns(ws);

                        // Repeat the header row at the top of every printed page.
                        ws.pageSetup.printTitlesRow = '1:1';

                        const buffer = await workbook.xlsx.writeBuffer();
                        FileSaver.saveAs(new Blob([buffer]), fileName);
                    }}
                >
                    <DownloadIcon fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
    );
}
